import {
  Button,
  Dialog,
  DialogClose,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  LoadingSm,
} from "@medimo/ui";
import { fetchMFAPreference, updateMFAPreference } from "aws-amplify/auth";
import { Check, Settings, Shield } from "lucide-react";
import type React from "react";
import { useState } from "react";

interface TOTPDisableDialogProps {
  onDisableComplete?: () => void;
  trigger?: React.ReactNode;
}

type SetupStep = "disable" | "complete";

export const TOTPDisableDialog = ({
  onDisableComplete,
}: TOTPDisableDialogProps) => {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<SetupStep>("disable");
  const [isLoading, setIsLoading] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(
    null,
  );

  const handleDialogClose = (isOpen: boolean) => {
    if (!isOpen && currentStep === "complete") {
      onDisableComplete?.();
    }
    setOpen(isOpen);
  };

  const handleDisableMFA = async () => {
    setIsLoading(true);
    await updateMFAPreference({
      email: "DISABLED",
      sms: "DISABLED",
      totp: "DISABLED",
    });
    const userMFAPreference = await fetchMFAPreference();
    if (userMFAPreference.enabled?.length) {
      setVerificationError("2要素認証の無効化に失敗しました。");
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
    setCurrentStep("complete");
  };

  const renderDisableStep = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          2要素認証を無効にしますか？
        </DialogTitle>
        <DialogCloseButton tabIndex={-1} />
      </DialogHeader>
      <DialogDescription className="text-gray-600 text-sm">
        2要素認証を無効にすると、セキュリティが低下する可能性があります。
        既存のワンタイムパスワードデバイスは無効化され、再度設定が必要になります。
      </DialogDescription>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="outline">キャンセル</Button>
        </DialogClose>
        {verificationError && (
          <p className="text-red-500 text-sm">{verificationError}</p>
        )}
        <Button
          variant="destructive"
          onClick={handleDisableMFA}
          disabled={isLoading}
          className="w-full"
        >
          {isLoading ? <LoadingSm /> : "無効化"}
        </Button>
      </DialogFooter>
    </>
  );

  const renderCompleteStep = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-green-600">
          <Shield className="h-5 w-5" />
          設定完了
        </DialogTitle>
        <DialogCloseButton />
      </DialogHeader>
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
        <Check className="h-8 w-8 text-green-600" />
      </div>

      <DialogDescription className="text-gray-600 text-sm">
        ご利用状況のページから再度有効化できます。
      </DialogDescription>
      <DialogFooter>
        <DialogClose asChild>
          <Button className="w-full">完了</Button>
        </DialogClose>
      </DialogFooter>
    </>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case "complete":
        return renderCompleteStep();
      default:
        return renderDisableStep();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <div className="flex">
        <DialogTrigger asChild>
          <Button
            variant="outline_destructive"
            size="sm"
            className="flex items-center gap-2 px-4"
          >
            <Settings className="h-4 w-4" />
            無効化
          </Button>
        </DialogTrigger>
      </div>
      <DialogContent
        className="sm:max-w-lg"
        onAnimationEnd={() => setCurrentStep("disable")}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (currentStep === "disable") {
              handleDisableMFA();
            } else if (currentStep === "complete") {
              handleDialogClose(false);
            }
          }
        }}
      >
        {renderCurrentStep()}
      </DialogContent>
    </Dialog>
  );
};
